import httpClient, { getBaseURL } from 'utility/httpClient.util'

export const REPORT_RESOURCE_MEDIA_GROUP = 'REPORT_STATIC'
export const REPORT_IMAGE_MAX_SIZE = 2 * 1024 * 1024
export const REPORT_PDF_MAX_SIZE = 10 * 1024 * 1024

export const createPdfTemplateResourceItem = (id) => ({
  id,
  page: 1,
  top: 0,
  left: 0,
  title: '',
  itemType: 'text',
  fieldName: '',
  defaultValue: '',
  value: 'PDF',
  name: 'PDF',
  type: 'pdf',
  width: '100%',
  height: '100%',
  fullPage: true,
  pdfPreviewMode: 'page',
})

export const getAttachmentId = (attachment) =>
  attachment?.attachmentId ||
  attachment?.mediaAttachmentId ||
  ''

export const getAttachmentFileName = (
  attachment,
  fallbackName = 'report-file',
) =>
  attachment?.originalFileName ||
  attachment?.titleName ||
  attachment?.name ||
  attachment?.fileName ||
  fallbackName

export const getAttachmentFileUrl = (attachmentOrId) => {
  const attachmentId =
    typeof attachmentOrId === 'string' || typeof attachmentOrId === 'number'
      ? String(attachmentOrId)
      : getAttachmentId(attachmentOrId)

  return attachmentId
    ? `${getBaseURL()}/media/attachment/originalFile/${attachmentId}`
    : ''
}

const unwrapAttachmentResponse = (response) => {
  let attachment = response?.data ?? response

  if (attachment?.data) {
    attachment = attachment.data
  }

  if (Array.isArray(attachment)) {
    attachment = attachment[0]
  }

  const attachmentId = getAttachmentId(attachment)

  return attachmentId ? { ...attachment, attachmentId } : attachment
}

const isSmallPdfPlaceholder = (item = {}) => {
  const isPixelSize = (value) =>
    typeof value === 'number' ||
    /^[0-9.]+(px)?$/i.test(String(value || '').trim())

  if (!isPixelSize(item.width) || !isPixelSize(item.height)) {
    return false
  }

  const width = parseFloat(item.width)
  const height = parseFloat(item.height)

  return (
    Number.isFinite(width) &&
    Number.isFinite(height) &&
    width <= 360 &&
    height <= 160
  )
}

export const uploadReportStaticFile = async (file, fallbackName = 'report-file') => {
  if (!(file instanceof Blob)) {
    throw new Error('ไม่พบไฟล์สำหรับอัปโหลด กรุณาเลือกรูปภาพหรือไฟล์ใหม่')
  }

  const formData = new FormData()
  formData.append('file', file)

  const response = (
    await httpClient.post(
      `/media/attachment/upload/${REPORT_RESOURCE_MEDIA_GROUP}?titleName=${encodeURIComponent(
        file?.name || fallbackName,
      )}`,
      formData,
      { headers: { 'content-type': 'multipart/form-data' } },
    )
  ).data

  const attachment = unwrapAttachmentResponse(response)
  if (!getAttachmentId(attachment)) {
    throw new Error('อัปโหลดสำเร็จแต่ไม่ได้รับ attachmentId')
  }

  return attachment
}

const getReportImageUploadFile = (item) => {
  const data = item.data
  if (data instanceof Blob) return data

  // JSON reports and restored drafts can retain a preview without a File.
  const dataUrl = [
    data?.preview,
    data?.base64,
    data,
    item.image?.base64,
    item.image,
  ].find((value) =>
    typeof value === 'string' && /^data:image\//i.test(value),
  )
  const match = dataUrl?.match(/^data:(image\/[a-z0-9.+-]+)(;base64)?,([\s\S]*)$/i)
  if (!match) {
    throw new Error('ไม่พบข้อมูลรูปภาพสำหรับอัปโหลด กรุณาเลือกรูปภาพใหม่')
  }

  const [, mimeType, encoding, content] = match
  const binary = encoding
    ? Uint8Array.from(atob(content), (character) => character.charCodeAt(0))
    : decodeURIComponent(content)
  const extension = mimeType
    .split('/')[1]
    .replace('svg+xml', 'svg')
    .replace('jpeg', 'jpg')

  return new File([binary], data?.name || `report-image.${extension}`, {
    type: mimeType,
  })
}

export const uploadReportImageResource = async (item) => {
  if (!item.data) return item

  const file = getReportImageUploadFile(item)
  if (file.size > REPORT_IMAGE_MAX_SIZE) {
    throw new Error('รูปภาพต้องมีขนาดไม่เกิน 2 MB')
  }
  const attachment = await uploadReportStaticFile(file, 'report-image')
  const nextItem = {
    ...item,
    image: {
      ...attachment,
      width: item.data?.width ?? item.image?.width,
      height: item.data?.height ?? item.image?.height,
    },
  }

  delete nextItem.data
  return nextItem
}

export const buildPdfResourceItem = (item, attachment, fallbackName = 'report.pdf') => {
  const attachmentId = getAttachmentId(attachment)
  const normalizedAttachment = { ...attachment, attachmentId }
  const nextItem = {
    ...item,
    pdf: normalizedAttachment,
    attachmentId,
    pdfPreviewMode: 'page',
    previewUrl: getAttachmentFileUrl(attachmentId),
    value: getAttachmentFileName(attachment, fallbackName),
  }

  if (isSmallPdfPlaceholder(item)) {
    nextItem.left = 0
    nextItem.top = 0
    nextItem.width = '100%'
    nextItem.height = '100%'
    nextItem.fullPage = true
  }

  delete nextItem.data
  return nextItem
}
